import { apiClient } from '@/lib/api-client';
import { problemCode } from '@/admin-area/api/problem';
import { readAssignableRoles } from '@/admin-area/roles/use-assignable-roles';

export type ImportCounts = {
  readonly cards: number;
  readonly cardUsers: number;
  readonly vehicles: number;
  readonly suppliers: number;
  readonly pendingCards: number;
  readonly pendingCardUsers: number;
  readonly pendingVehicles: number;
  /** Not yet among the users: their cards and vehicles link at their first call (decision 148). */
  readonly unmatchedEmails: readonly string[];
  /** Unmatched too, and ignored: nothing pending on them links. */
  readonly ignoredEmails: readonly string[];
  /** Payment methods read as neither a card, a transfer nor cash: never offered. */
  readonly otherMethodLabels: readonly string[];
};

export type ImportOutcome =
  | { readonly ok: true; readonly counts: ImportCounts }
  | { readonly ok: false; readonly status: number; readonly message: string | null };

/** Sends the file's JSON as it is. The file's content is never kept anywhere. */
export async function importReferenceLists(body: Record<string, unknown>): Promise<ImportOutcome> {
  try {
    const { data, error, response } = await apiClient.POST('/v1/expenses/reference/import', {
      // The contract types the file as an open object; it goes as it is.
      body: body as Record<string, never>,
    });
    if (response.ok && data !== undefined) return { ok: true, counts: data };
    const problem =
      typeof error === 'object' && error !== null ? (error as Record<string, unknown>) : {};
    const text = problem['detail'] ?? problem['title'];
    return { ok: false, status: response.status, message: typeof text === 'string' ? text : null };
  } catch {
    return { ok: false, status: 0, message: null };
  }
}

/** «Ignora» (`ignored: true`) or «Annulla» beside an unmatched email. True when done. */
export async function setIgnoredEmail(email: string, ignored: boolean): Promise<boolean> {
  try {
    const { response } = await apiClient.POST('/v1/expenses/reference/ignored-emails', {
      body: { email, ignored },
    });
    return response.ok;
  } catch {
    return false;
  }
}

export type InviteOutcome = 'invited' | 'already-pending' | 'forbidden' | 'failed';

/** The «Note spese» requester role's code in the role list (its label is «Richiedente»). */
const REQUESTER_ROLE_CODE = 'expenses-submitter';
const EXPENSES_MODULE_CODE = 'expenses';

/**
 * «Invita» beside an unmatched email: the administration area's own invitation route, as the
 * signed-in caller, at the active organization, offering the «Richiedente» role of «Note spese»
 * (ADR-019: finance never invites on its own authority). The role comes from the same
 * assignable-roles list the administration area's role picker reads.
 */
export async function inviteRequester(
  organizationId: string,
  email: string,
): Promise<InviteOutcome> {
  try {
    const roles = await readAssignableRoles('organizations', organizationId);
    if (roles.response.status === 403) return 'forbidden';
    if (!roles.response.ok || roles.data === undefined) return 'failed';
    const requester = roles.data.find(
      (role) => role.module?.code === EXPENSES_MODULE_CODE && role.code === REQUESTER_ROLE_CODE,
    );
    if (requester === undefined) return 'failed';
    // `memberTag` is read by the server only when the role admits both tags. «org»: the
    // organization's own people.
    const body = { email, roleIds: [requester.id], memberTag: 'org' as const };
    const { error, response } = await apiClient.POST(
      '/v1/administration/{placeKind}/{placeId}/invitations',
      { params: { path: { placeKind: 'organizations', placeId: organizationId } }, body },
    );
    if (response.ok) return 'invited';
    if (response.status === 403) return 'forbidden';
    if (response.status === 409 && problemCode(error) === 'INVITATION_ALREADY_PENDING') {
      return 'already-pending';
    }
    return 'failed';
  } catch {
    return 'failed';
  }
}
